import {createSelector} from 'reselect';
import * as appConstants from '../../constants/ApplicationConstants';

import moment from 'moment';

const getSelectedInvoices = state => state.invoice.selected;
const getLineItems = state => state.invoice.invoiceDetail;
const getPaymentOptions = state => state.default.payerDetails.payment_cards;
const getSelectedPaymentOptionIndex = state => state.default.selectedCard;
const getCurrentUser = state => state.default.currentUser;
const getAccountType = state => state.user_options.accountType;
const getSelectedSecondaryAccountIndex = state => state.user_options.selectedSecondaryAccount;
const getSelectedPrimaryAccountIndex = state => state.user_options.selectedPrimaryAccountIndex;
const getPrimaryAccounts = state => state.user_options.primaryAccounts;
const getSecondaryAccounts = state => state.user_options.secondaryAccounts;
const getInvoiceSearchToDate = state => state.user_options.invoiceSearchToDate;
const getInvoiceSearchFromDate = state => state.user_options.invoiceSearchFromDate;
const getInvoiceSearchStatus = state => state.user_options.invoiceSearchStatus;
const getInvoiceSearchDueDateFrom = state => state.user_options.invoiceSearchDueDateFrom;
const getInvoiceSearchDueDateTo = state => state.user_options.invoiceSearchDueDateTo;
const getInvoiceSearchFilter = state => state.user_options.filter;
const _getImpersonatedUserId = state => state.user_options.impersonatedUserId;
const getCnbsConfig = state => state.app.cnbsConfig;


//Added
const getSoldToSearchParams = (state) => state.user_options.SoldTo_SearchParams;
const getCurrency = (state) => state.default.selectedCurrencyOptionValue;





export const getImpersonatedUserId = createSelector([getCurrentUser, _getImpersonatedUserId], (user, impersonatedUserId) => {
  if (impersonatedUserId === null || typeof impersonatedUserId === "undefined") {
    return user.UserId;
  }
  return impersonatedUserId;
});

export const getInvoiceTotal = createSelector([getSelectedInvoices], items => {
  let total = Object.keys(items)
    .map(key => items[key])
    .reduce((total, item) => {
      total += item.open_amount;
      return Math.round(total * 100) / 100;
    }, 0);
  return total.toFixed(2);
});

export const getSelectedPrimaryAccount = createSelector(
  [getSelectedPrimaryAccountIndex, getPrimaryAccounts],
  (index, accounts) => {
    if (typeof accounts === "undefined" || accounts.length === 0) {
      return null;
      }
      if (index >= accounts.length)
          index = 0;
    return accounts[index];
  }
);

export const getCanUseChecks = createSelector(
  [getSelectedPrimaryAccount],
  (account) => {
    if(account == null){
      return true;
    }
    return account.CompanyCode !== "0020";
  }
);

export const getSelectedSecondaryAccount = createSelector(
  [getSelectedSecondaryAccountIndex, getSecondaryAccounts],
  (index, accounts) => {
    if (typeof accounts === "undefined" || accounts == null || accounts.length === 0) {
      return null;
    }
    return accounts[index];
  }
);

export const getPayTotal = createSelector([getLineItems], items => {
  let total = Object.keys(items)
    .map(key => items[key])
    .reduce((total, item) => {
      total += parseFloat(item.amount);
      return (Math.round(parseFloat(total) * 100) / 100);
    }, 0);
  return total.toFixed(2);
});

// Represents the amount the will be paid with credit card or EC.
export const getBeforeCreditTotal = createSelector([getLineItems, getSelectedInvoices], (items, selected) => {
  return Object.keys(items)
    .map(key => {
      return {details: items[key], invoice: selected[key]}
    })
    .reduce((total, item) => {
      if (item.invoice.open_amount >= 0) {
        total += item.details.amount;
      }
      return total;
    }, 0);
});

export const getCreditTotal = createSelector([getLineItems, getSelectedInvoices], (items, selected) => {
  return Object.keys(items)
    .map(key => {
      return {details: items[key], invoice: selected[key]}
    })
    .reduce((total, item) => {
      if (item.invoice.open_amount < 0) {
        total += item.details.amount;
      }
      return total;
    }, 0);
});

export const getSelectedInvoiceKeys = createSelector([getSelectedInvoices], items => {
  return Object.keys(items);
});

export const getOverpayingAmount = createSelector([getInvoiceTotal, getPayTotal], (total, payTotal) => {
  return (Math.round(parseFloat(payTotal - total) * 100) / 100).toFixed(2);
});

export const getPayerAccount = createSelector(
  [getSelectedPrimaryAccount, getSelectedSecondaryAccount, getAccountType],
  (primary, secondary, accountType) => {
    if (accountType === appConstants.SOLD_TO_COMPARE) {
      return secondary;
    } else {
      return primary;
    }
  }
);


export const getAllPaymentOptions = createSelector(
  [getPaymentOptions, state => state.payment.sessionCards, state => state.payment.sessionChecks, getPayerAccount],
  (options, sessionCards, sessionChecks, payerAccount) => {
    let newCardData = options.slice(0);
    newCardData.forEach(c => c.is_session = false);

    if (payerAccount) {
      let account = payerAccount.PrimaryAcct;
      for (let prop in sessionCards[account]) {
        if (sessionCards[account].hasOwnProperty(prop)) {
          let card = sessionCards[account][prop];
          card.is_session = true;
          newCardData.push(sessionCards[account][prop]);
        }
      }

      for (let prop in sessionChecks[account]) {
        if (sessionChecks[account].hasOwnProperty(prop)) {
          let check = sessionChecks[account][prop];
          check.is_session = true;
          newCardData.push(sessionChecks[account][prop]);
        }
      }
    }

    return newCardData;
  }
);

export const getSelectedPaymentOption = createSelector(
  [getAllPaymentOptions, getSelectedPaymentOptionIndex],
  (options, selectedIndex) => {
    if (selectedIndex === null) {
      return null;
    }
    return options[selectedIndex];
  }
);

export const getCustomerNumber = createSelector(
  [getAccountType, getSelectedSecondaryAccount, getSelectedPrimaryAccount],
  (accountType, selectedSecondaryAccount, selectedPrimaryAccount) => {
    if (accountType === appConstants.SOLD_TO_COMPARE) {
      return selectedSecondaryAccount.PrimaryAcct;
    } else {
      return selectedPrimaryAccount.PrimaryAcct;
    }
  }
);

//TODO move out of selectors.
export function getPayerSoldToForInvoiceQuery(accountType, secondaryAccount, primaryAccount) {
  let payer_data;
  let soldto_data = {customer_number: "", company_code: ""};
  if (accountType === appConstants.SOLD_TO_COMPARE) {
    payer_data = {
      customer_number: secondaryAccount.PrimaryAcct,
      company_code: primaryAccount.CompanyCode
    };
    soldto_data = {
      customer_number: primaryAccount.PrimaryAcct,
      accounts: primaryAccount.TransactionalAccounts,
    };
  } else {
    payer_data = {
      customer_number: primaryAccount.PrimaryAcct,
      company_code: primaryAccount.CompanyCode
    };
    soldto_data = null;
  }
  return {payer_data, soldto_data};
}

//TODO move out of selectors.
export function getDefaultFilters(accountType, impersonatedUserId) {
  let defaultFilters = [];
  if (accountType === appConstants.ACCOUNT_PAYER_ALL) {
    defaultFilters.push({
      filter_type: '90',
      value: impersonatedUserId,
    });
  }
  return defaultFilters;
}


export const getInvoiceSearchQuery = createSelector(
    [
        getAccountType,
        getSoldToSearchParams,
        getSelectedPrimaryAccount,
        getSelectedSecondaryAccount,
        getInvoiceSearchFromDate,
        getInvoiceSearchToDate,
        getInvoiceSearchStatus,
        getInvoiceSearchDueDateFrom,
        getInvoiceSearchDueDateTo,
        getInvoiceSearchFilter,
        getImpersonatedUserId,
    ],
    (
        accountType,
        SoldToSearchParams,
        primaryAccount,
        secondaryAccount,
        fromDate,
        toDate,
        status,
        dueDateFrom,
        dueDateTo,
        filter,
        impersonatedUserId
    ) => {
        try {
            let { payer_data, soldto_data } = getPayerSoldToForInvoiceQuery(
                accountType,
                secondaryAccount,
                primaryAccount
            );
            return {
                payer_data,
                soldto_data,
                search_parameters: {
                    document_type: "01",
                    status: status,
                    SoldTo_SearchParams: SoldToSearchParams,
                    date_from:
                        fromDate === "" ? appConstants.INV_LIST_REQ_BEGIN_DATE : fromDate,
                    date_to:
                        toDate === "" ? moment(new Date()).format("MM-DD-YYYY") : toDate,
                    due_date_from: dueDateFrom,
                    due_date_to: dueDateTo,
                    currency_key: "",
                    filter: getDefaultFilters(accountType, impersonatedUserId).concat(
                        filter
                    ),
                },
            } ;
        } catch (e) {
            return null;
        }
    }
);



export const getInvoiceQuery = createSelector(
  [
    getAccountType,
    getSelectedPrimaryAccount,
    getSelectedSecondaryAccount,
    getInvoiceSearchFromDate,
    getInvoiceSearchToDate,
    getInvoiceSearchStatus,
    getInvoiceSearchDueDateFrom,
    getInvoiceSearchDueDateTo,
    getInvoiceSearchFilter,
    getImpersonatedUserId,
  ],
  (
    accountType,
    primaryAccount,
    secondaryAccount,
    fromDate,
    toDate,
    status,
    dueDateFrom,
    dueDateTo,
    filter,
    impersonatedUserId
  ) => {
    try {
      let {payer_data, soldto_data} = getPayerSoldToForInvoiceQuery(accountType, secondaryAccount, primaryAccount);
      return {
        payer_data,
        soldto_data,
        search_parameters: {
          document_type: '01',
          status: '01',
          date_from: appConstants.INV_LIST_REQ_BEGIN_DATE,
          date_to: moment(new Date()).format('MM-DD-YYYY'),
          due_date_from: dueDateFrom,
          due_date_to: dueDateTo,
          currency_key: '',
          filter: getDefaultFilters(accountType, impersonatedUserId).concat(filter),
        }
      };
    } catch (e) {
      return null;
    }
  }
);

export const getDepositReasonCodes = createSelector([getCnbsConfig], config => {
  const reasonCodes = [];
  if (config && config.payment_reason_codes) {
    for (let i = 0; i < config.payment_reason_codes.length; i += 1) {
      const code = config.payment_reason_codes[i];
      if (code.payment_type === 'D') {
        reasonCodes.push({
          value: code.reason_code,
          label: code.description,
          commentsRequired: code.comments_required,
        });
      }
    }
  }
  return reasonCodes;
});

export const getPayerAccounts = createSelector(
  [getPrimaryAccounts, getSecondaryAccounts, getAccountType],
  (primaryAccounts, secondaryAccounts, accountType) => {
    if (accountType === appConstants.SOLD_TO_COMPARE) {
      return secondaryAccounts;
    } else {
      return primaryAccounts;
    }
  }
);

export const hasDeposits = createSelector(
  [getCnbsConfig],
  (config) => {
    try {
      return typeof config && config.payment_types && config.payment_types.find(t => t.payment_type === "D") !== "undefined" && config.payment_types.find(t => t.payment_type === "D").active === "X";
    } catch (e) {
      return false;
    }
  }
);


export const getCurrentAccount = createSelector(
  [getSelectedPrimaryAccount, getSelectedSecondaryAccount, getAccountType],
  (primary, secondary, accountType) => {
    let request;
    if (accountType === appConstants.SOLD_TO_COMPARE) {
      // Use Secondary Account
      request = {
        company_code: primary && primary.CompanyCode,
        sales_organization: secondary && secondary.SalesOrganization,
        distribution_channel: secondary && secondary.DistributionChannel,
        division: secondary && secondary.Division
      };
    } else {
      // Use Primary Account
      request = {
        company_code: primary && primary.CompanyCode,
        sales_organization: primary && primary.SalesOrganization,
        distribution_channel: primary && primary.DistributionChannel,
        division: primary && primary.Division
      };
    }
    return request;
  }
);
